import { fn, get } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { ensureSafeComponent } from '@embroider/util';
import { tracked } from '@glimmer/tracking';
import Input from 'ember-power-select/components/power-select-multiple/input';
import EmberPowerSelectMultipleTrigger from 'ember-power-select/components/power-select-multiple/trigger';
import { and, eq, lte, not, or } from 'ember-truth-helpers';
import orElse from 'volta/helpers/or-else';
import BvIcon from 'volta/components/bv-icon';
import BvSpinner from 'volta/components/bv-spinner';
import BvTag from 'volta/components/bv-tag';

import type { ComponentLike, ContentValue, WithBoundArgs } from '@glint/template';
import type { BvBtnArgs } from 'volta/components/bv-btn';

interface BvSelectMultiTriggerSignature {
  Args: Omit<EmberPowerSelectMultipleTrigger['args'], 'Element'> & {
    isRequired?: boolean;
    isLoading?: boolean;
    verticalPosition?: 'auto' | 'above' | 'below';
    size?: BvBtnArgs['size'];
    searchSuffix?: ContentValue;
    selectedPath?: string;
    fill?: boolean;
  };
  Element: HTMLElement;
}

type BvSelectMultiTriggerLike = WithBoundArgs<
  ComponentLike<BvSelectMultiTriggerSignature>,
  'select' | 'searchEnabled' | 'searchField' | 'ariaActiveDescendant' | 'buildSelection'
>;

export default class BvSelectMultiTrigger extends EmberPowerSelectMultipleTrigger {
  <template>
    {{! @glint-nocheck: not typesafe yet }}
    {{! template-lint-disable no-invalid-interactive }}
    {{! template-lint-disable no-unsupported-role-attributes }}
    {{! template-lint-disable require-aria-activedescendant-tabindex }}
    {{! template-lint-disable no-pointer-down-event-binding }}
    <div
      id='ember-power-select-multiple-options-{{@select.uniqueId}}'
      aria-activedescendant={{if (and @select.isOpen (not @searchEnabled)) @ariaActiveDescendant}}
      class='bv-select-multi__options'
      {{this.openChange @select.isOpen}}
      {{on 'touchstart' this.chooseOption}}
      {{on 'mousedown' this.chooseOption}}
      ...attributes
    >
      {{#each @select.selected as |opt idx|}}
        <BvTag
          data-selected-index={{idx}}
          class='bv-select-multi__option'
          @size='sm'
          @color={{orElse opt.color 'primary'}}
          @disabled={{orElse opt.disabled @select.disabled}}
          @onRemove={{if
            (not
              (or
                (and @isRequired (lte @select.selected.length 1))
                (orElse opt.disabled @select.disabled)
              )
            )
            (fn this.deleteOption opt)
          }}
        >
          {{#if @selectedPath}}
            {{get opt @selectedPath}}
          {{else if @selectedItemComponent}}
            {{#let
              (component (ensureSafeComponent @selectedItemComponent this))
              as |SelectedItemComponent|
            }}
              <SelectedItemComponent @extra={{@extra}} @option={{opt}} @select={{@select}} />
            {{/let}}
          {{else}}
            {{yield opt @select}}
          {{/if}}
        </BvTag>
      {{else}}
        {{#if (and @placeholder (not @searchEnabled))}}
          {{#let
            (component (ensureSafeComponent @placeholderComponent this))
            as |PlaceholderComponent|
          }}
            <PlaceholderComponent @placeholder={{@placeholder}} />
          {{/let}}
        {{/if}}
      {{/each}}
      {{#if @searchEnabled}}
        <Input
          class='bv-select-multi__input'
          @select={{@select}}
          @ariaActiveDescendant={{@ariaActiveDescendant}}
          @ariaLabelledBy={{@ariaLabelledBy}}
          @ariaLabel={{@ariaLabel}}
          @listboxId={{@listboxId}}
          @tabindex={{@tabindex}}
          @buildSelection={{@buildSelection}}
          @placeholder={{@placeholder}}
          @placeholderComponent={{@placeholderComponent}}
          @searchField={{@searchField}}
          @onFocus={{@onFocus}}
          @onBlur={{@onBlur}}
          @onKeydown={{@onKeydown}}
          @onInput={{@onInput}}
        />
      {{/if}}
      <div class='bv-select-multi__trigger-status'>
        {{#if @isLoading}}
          <BvSpinner @size='xs' />
        {{else}}
          {{#if (and @searchEnabled @searchSuffix)}}
            {{@searchSuffix}}
          {{/if}}
          <BvIcon
            @glyph={{if
              (eq @verticalPosition 'above')
              'caret-up'
              (if (eq @verticalPosition 'below') 'caret-down' 'select')
            }}
            @size={{if (or (eq @size 'md') (not @size)) 'sm' @size}}
          />
        {{/if}}
      </div>
    </div>
  </template>
  @tracked focused = false;

  @action
  deleteOption(option: any) {
    this.args.select?.actions.choose(option);
    return false;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'BvSelectMulti::Trigger': BvSelectMultiTriggerLike;
    'bv-select-multi/trigger': BvSelectMultiTriggerLike;
  }
}
